// firebase/order-functions.js
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    startAfter,
} from "firebase/firestore";
import { db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";



/**
 * Get orders for a specific store
 * @param {string} storeId - The ID of the store
 * @param {object} options - Query options (limit, lastDoc, filters)
 * @returns {Promise<object>} Object with orders array and last document for pagination
 */
export const getStoreOrders = async (storeId, options = {}) => {
    try {
        if (!storeId) {
            throw new Error("Store ID is required to get orders");
        }

        const { limit: queryLimit = 20, lastDoc = null, status = null, sortBy = 'createdAt', sortDirection = 'desc' } = options;

        const ordersRef = collection(db, "stores", storeId, "orders");

        let queryConstraints = [];

        if (status) {
            queryConstraints.push(where("status", "==", status));
        }

        queryConstraints.push(orderBy(sortBy, sortDirection));
        queryConstraints.push(limit(queryLimit));

        if (lastDoc) {
            queryConstraints.push(startAfter(lastDoc));
        }

        const ordersQuery = query(ordersRef, ...queryConstraints);
        const querySnapshot = await getDocs(ordersQuery);

        const orders = [];
        let lastVisible = null;

        querySnapshot.forEach((doc) => {
            orders.push({
                id: doc.id,
                ...doc.data()
            });
            lastVisible = doc;
        });

        return {
            orders,
            lastDoc: lastVisible
        };
    } catch (error) {
        throw error;
    }
};


/**
 * Update the status of an order with improved error handling and permissions check
 * @param {string} storeId - The store ID
 * @param {string} orderId - The order ID to update
 * @param {string} status - The new status
 * @returns {Promise<{success: boolean, message: string}>} Result of the update
 */
export const updateOrderStatus = async (storeId, orderId, status) => {
    try {
        const updateOrderStatusFunction = httpsCallable(functions, 'updateOrderStatus');
        const result = await updateOrderStatusFunction({
            storeId,
            orderId,
            status
        });

        return result.data;
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Failed to update order status'
        };
    }
};

/**
 * Fetch orders for a specific customer from a specific store
 * @param {string} userId - The customer's user ID
 * @param {string} activeStoreId - The store ID to fetch orders from
 * @returns {Promise<Array>} Array of order objects with full details
 */
export const getCustomerOrders = async (userId, activeStoreId) => {
    try {
        if (!userId || !activeStoreId) {
            return [];
        }

        // First, get references from user's orders collection with store filter
        const userOrdersRef = collection(db, "users", userId, "orders");
        // Add filter to only get orders from the active store
        const userOrdersQuery = query(
            userOrdersRef,
            where("storeId", "==", activeStoreId),
            orderBy("createdAt", "desc")
        );
        const userOrdersSnapshot = await getDocs(userOrdersQuery);

        if (userOrdersSnapshot.empty) {
            return [];
        }

        // Process the user's order references
        const userOrders = userOrdersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Now fetch the full order details from the store's orders collection
        const fullOrdersData = [];

        for (const userOrder of userOrders) {
            try {
                // Verify this order belongs to the active store
                if (userOrder.storeId !== activeStoreId) {
                    continue;
                }

                // If user order has storeOrderId and storeId, get full order details
                if (userOrder.storeOrderId) {
                    const storeOrderRef = doc(db, "stores", activeStoreId, "orders", userOrder.storeOrderId);
                    const storeOrderDoc = await getDoc(storeOrderRef);

                    if (storeOrderDoc.exists()) {
                        const storeData = storeOrderDoc.data();
                        fullOrdersData.push({
                            id: userOrder.id, // Keep the user's order ID for reference
                            storeOrderId: userOrder.storeOrderId, // Store the reference to store's order
                            storeId: activeStoreId, // Explicitly include the store ID
                            ...storeData
                        });
                    } else {
                        // If store order not found, fall back to user order data
                        fullOrdersData.push({
                            ...userOrder,
                            storeId: activeStoreId
                        });
                    }
                } else if (userOrder.orderId) {
                    // For orders created with the old system (where orderId was set but not storeOrderId)
                    const storeOrderRef = doc(db, "stores", activeStoreId, "orders", userOrder.orderId);
                    const storeOrderDoc = await getDoc(storeOrderRef);

                    if (storeOrderDoc.exists()) {
                        const storeData = storeOrderDoc.data();
                        fullOrdersData.push({
                            id: userOrder.id,
                            storeOrderId: userOrder.orderId,
                            storeId: activeStoreId,
                            ...storeData
                        });
                    } else {
                        // Fall back to user data
                        fullOrdersData.push({
                            ...userOrder,
                            storeId: activeStoreId
                        });
                    }
                } else {
                    // If we have neither storeOrderId nor orderId, just use user data
                    fullOrdersData.push({
                        ...userOrder,
                        storeId: activeStoreId
                    });
                }
            } catch (err) {
                // Still include the user order data we have
                fullOrdersData.push({
                    ...userOrder,
                    storeId: activeStoreId
                });
            }
        }

        return fullOrdersData;
    } catch (error) {
        throw error;
    }
};